/**
 * Panel de transcripción en vivo para el Call Sidebar.
 * Colapsable para dejar espacio al cuestionario. Muestra burbujas agente/cliente.
 */

import { useRef, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import type { TranscriptionSegment, TranscriptionWSStatus } from '@/types/transcription';

// ─── Status indicator ────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<TranscriptionWSStatus, { color: string; label: string }> = {
  idle: { color: 'bg-gray-400', label: 'Sin conexión' },
  connecting: { color: 'bg-yellow-400 animate-pulse', label: 'Conectando...' },
  connected: { color: 'bg-red-500 animate-pulse', label: 'Transcribiendo' },
  disconnected: { color: 'bg-gray-400', label: 'Desconectado' },
  error: { color: 'bg-red-600', label: 'Error' },
};

// ─── Props ───────────────────────────────────────────────────────────────────

interface CallSidebarTranscriptionProps {
  segments: TranscriptionSegment[];
  wsStatus: TranscriptionWSStatus;
  isCapturing: boolean;
  error: string | null;
  /** Empezar colapsado por defecto */
  defaultCollapsed?: boolean;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function CallSidebarTranscription({
  segments,
  wsStatus,
  isCapturing,
  error,
  defaultCollapsed = false,
}: CallSidebarTranscriptionProps) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const scrollRef = useRef<HTMLDivElement>(null);
  const finalCount = segments.filter((s) => s.isFinal).length;

  // Auto-scroll al fondo cuando llegan nuevos segmentos
  useEffect(() => {
    if (!collapsed && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [segments, collapsed]);

  // Auto-expandir cuando llega primer segmento
  useEffect(() => {
    if (segments.length > 0 && collapsed) {
      setCollapsed(false);
    }
  }, [segments.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const status = STATUS_CONFIG[wsStatus] ?? STATUS_CONFIG.idle;

  return (
    <div className="border-b border-gray-200">
      {/* Toggle header */}
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-2 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className={cn('w-2 h-2 rounded-full shrink-0', status.color)} />
          <span className="text-xs font-semibold text-gray-700">Transcripción</span>
          {isCapturing && (
            <span className="text-[10px] text-gray-400">{status.label}</span>
          )}
          {finalCount > 0 && (
            <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 rounded-full">
              {finalCount}
            </span>
          )}
        </div>
        {collapsed ? (
          <ChevronDownIcon className="h-3.5 w-3.5 text-gray-400" />
        ) : (
          <ChevronUpIcon className="h-3.5 w-3.5 text-gray-400" />
        )}
      </button>

      {/* Transcript area */}
      <AnimatePresence>
        {!collapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div
              ref={scrollRef}
              className="max-h-40 overflow-y-auto px-4 pb-3 space-y-1.5"
            >
              {segments.length === 0 && !error && (
                <p className="text-xs text-gray-400 text-center py-3">
                  {isCapturing ? 'Esperando audio...' : 'La transcripción aparecerá aquí'}
                </p>
              )}

              {error && (
                <p className="text-xs text-red-500 text-center py-2">
                  {error}
                </p>
              )}

              {segments.map((seg) => (
                <div
                  key={seg.id}
                  className={cn(
                    'flex',
                    seg.channel === 'agent' ? 'justify-start' : 'justify-end',
                  )}
                >
                  <div
                    className={cn(
                      'max-w-[85%] rounded-lg px-2.5 py-1 text-xs leading-relaxed',
                      seg.channel === 'agent'
                        ? 'bg-blue-50 text-blue-900'
                        : 'bg-green-50 text-green-900',
                      !seg.isFinal && 'opacity-50 italic',
                    )}
                  >
                    <span className={cn(
                      'font-semibold text-[10px] block',
                      seg.channel === 'agent' ? 'text-blue-500' : 'text-green-500',
                    )}>
                      {seg.channel === 'agent' ? 'Agente' : 'Cliente'}
                    </span>
                    {seg.text}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
